/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Structured Logger for TORQE Frontend
 *
 * Provides consistent logging with log levels and context. In addition to
 * console output, INFO+ events are forwarded to the backend log relay
 * (`logRelay`) which ships them to the platform LGTM stack as
 * `service.name=torqe-frontend`. The relay is a no-op until wired at startup
 * via `initLogRelay`, so dev/tests are unaffected.
 */

import { logRelay, type RelayLevel } from "@/lib/logRelay";
import { getCorrelationId } from "@/lib/correlationId";

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogContext {
  [key: string]: unknown;
}

const RELAY_LEVEL: Record<string, RelayLevel> = {
  DEBUG: "debug",
  INFO: "info",
  WARN: "warn",
  ERROR: "error",
};

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private formatMessage(
    level: string,
    category: string,
    message: string,
    context?: LogContext
  ): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : "";
    return `[${timestamp}] [${level}] [${category}] ${message}${contextStr}`;
  }

  debug(category: string, message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage("DEBUG", category, message, context));
    }
  }

  info(category: string, message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage("INFO", category, message, context));
    }
    this.relay("INFO", category, message, context);
  }

  warn(category: string, message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage("WARN", category, message, context));
    }
    this.relay("WARN", category, message, context);
  }

  error(category: string, message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage("ERROR", category, message, context));
    }
    this.relay("ERROR", category, message, context);
  }

  /** Forward an event to the backend relay (no-op until wired at startup). */
  private relay(level: string, category: string, message: string, context?: LogContext): void {
    logRelay.enqueue(RELAY_LEVEL[level] ?? "info", message, {
      category,
      // Same session id the API client sends as x-correlation-id, so relayed
      // logs thread with the backend spans/logs they triggered (LGTM doc §8).
      correlationId: getCorrelationId(),
      context,
    });
  }

  // Convenience method for logging objects
  logObject(category: string, label: string, obj: unknown): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`[${category}] ${label}:`, obj);
    }
  }
}

// Singleton instance
export const logger = new Logger();

// Category constants for consistency
export const LogCategory = {
  MSAL: "MSAL",
  AUTH: "AUTH",
  TOKEN: "TOKEN",
  API: "API",
  APP: "APP",
  LOGIN: "LOGIN",
} as const;
